import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {fetchAuthSession} from 'aws-amplify/auth'
import {getConfig} from './config'
import {getLogger} from './logging'

const {paymentInstallmentsApiUrl} = getConfig()
const logger = getLogger('paymentInstallmentsApi')

/**
 * Payment schedule item from the payment-installments API
 */
export interface PaymentScheduleItem {
  folioId: string
  chargeDate: string
  folioBalance: {
    amount: number
    currency: string
  }
  billingPeriod: {
    startDate: string
    endDate: string
    formattedPeriod: string
    installmentLabel: string
  }
  routing: {
    id: string
    filter: {
      from: string
      to: string
    }
  }
}

export interface PaymentPlanResponse {
  isPaymentScheduleEligible: boolean
  ineligibleMessage: string | null
  paymentScheduleItem: any | null
  hasPaymentSchedule: boolean
  reservation?: any
}

export interface PaymentScheduleResponse {
  schedule: PaymentScheduleItem[]
}

/**
 * Payment history item - represents a folio with its billing period and payments
 * This includes ALL installments (including the first one paid at booking)
 */
export interface PaymentHistoryItem {
  folioId: string
  mainFolio: boolean
  payments: any[]
  billingPeriod: {
    startDate: string
    endDate: string
    formattedPeriod: string
    installmentLabel: string
  }
}

export interface PaymentHistoryResponse {
  folioPayments: PaymentHistoryItem[]
}

/**
 * Billing period item - represents a billing period for an installment
 * This includes ALL installments (main folio + all routings)
 */
export interface BillingPeriodItem {
  folioId: string
  installmentNumber: number
  billingPeriod: {
    startDate: string
    endDate: string
    formattedPeriod: string
    installmentLabel: string
  }
  isMainFolio: boolean
  routingId?: string
}

export interface BillingPeriodsResponse {
  reservationId: string
  arrival: string
  departure: string
  totalInstallments: number
  billingPeriods: BillingPeriodItem[]
}

export const paymentInstallmentsApi = createApi({
  reducerPath: 'paymentInstallmentsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: paymentInstallmentsApiUrl,
    prepareHeaders: async (headers): Promise<Headers> => {
      try {
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken?.toString()
        if (idToken) {
          headers.set('Authorization', `Bearer ${idToken}`)
        }
      } catch (e) {
        logger.error({
          message: 'Error occurred getting token for payment installments API',
          errorData: {error: e}
        })
      }
      return headers
    }
  }),
  tagTypes: ['PaymentSchedule'],
  endpoints: builder => ({
    // Check if a reservation has a payment schedule or is eligible for one
    getPaymentPlan: builder.query<PaymentPlanResponse, {reservationId: string}>(
      {
        query({reservationId}) {
          return `/guest/reservations/${reservationId}`
        },
        transformResponse: (response: any): PaymentPlanResponse => {
          return {
            isPaymentScheduleEligible:
              response.isPaymentScheduleEligible || false,
            ineligibleMessage: response.ineligibleMessage || null,
            paymentScheduleItem: response.paymentScheduleItem || null,
            hasPaymentSchedule: !!response.paymentScheduleItem,
            reservation: response.reservation
          }
        },
        providesTags: ['PaymentSchedule']
      }
    ),
    // Get the payment schedule for a reservation (future payments only)
    getPaymentSchedule: builder.query<
      PaymentScheduleResponse,
      {reservationId: string}
    >({
      query({reservationId}) {
        return `/guest/reservations/${reservationId}/payment-schedule`
      },
      transformResponse: (
        response: PaymentScheduleItem[]
      ): PaymentScheduleResponse => {
        const schedule = response.map((item, index) => ({
          folioId: item.folioId,
          chargeDate: item.chargeDate,
          folioBalance: item.folioBalance,
          billingPeriod: {
            startDate: (item as any).routing?.filter?.from || '',
            endDate: (item as any).routing?.filter?.to || '',
            formattedPeriod: formatBillingPeriod(
              (item as any).routing?.filter?.from,
              (item as any).routing?.filter?.to
            ),
            installmentLabel: getInstallmentLabel(index, response.length)
          },
          routing: item.routing
        }))
        return {schedule}
      },
      providesTags: ['PaymentSchedule']
    }),
    // Get payment history for a reservation (ALL installments including first)
    getPaymentHistory: builder.query<
      PaymentHistoryResponse,
      {reservationId: string}
    >({
      query({reservationId}) {
        return `/guest/reservations/${reservationId}/payment-history`
      },
      transformResponse: (response: any): PaymentHistoryResponse => {
        const folioPayments = (response.folioPayments || []).map(
          (item: any, index: number, arr: any[]) => {
            const startDate = item.mainFolio
              ? item.filter?.from
              : item.routing?.filter?.from
            const endDate = item.mainFolio
              ? item.filter?.to
              : item.routing?.filter?.to

            return {
              folioId: item.folioId,
              mainFolio: item.mainFolio || false,
              payments: item.payments || [],
              billingPeriod: {
                startDate: startDate || '',
                endDate: endDate || '',
                formattedPeriod: formatBillingPeriod(startDate, endDate),
                installmentLabel: getInstallmentLabelForHistory(
                  index,
                  arr.length
                )
              }
            }
          }
        )
        return {folioPayments}
      },
      providesTags: ['PaymentSchedule']
    }),
    // Get ALL billing periods for a reservation (main folio + all routings)
    // This is the recommended endpoint for getting billing periods for invoices
    getBillingPeriods: builder.query<
      BillingPeriodsResponse,
      {reservationId: string}
    >({
      query({reservationId}) {
        return `/guest/reservations/${reservationId}/billing-periods`
      },
      providesTags: ['PaymentSchedule']
    })
  })
})

/**
 * Formats a billing period as a readable string.
 */
function formatBillingPeriod(startDate: string, endDate: string): string {
  if (!startDate || !endDate) {
    return ''
  }
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }
  const start = new Date(startDate)
  const end = new Date(endDate)
  const startFormatted = start.toLocaleDateString('en-US', formatOptions)
  const endFormatted = end.toLocaleDateString('en-US', formatOptions)

  return `${startFormatted} - ${endFormatted}`
}

/**
 * Gets the installment label based on index (for payment schedule - future payments only).
 */
function getInstallmentLabel(index: number, total: number): string {
  if (index === total - 1) {
    return 'Final Installment'
  }
  const ordinals = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth'
  ]
  return `${ordinals[index] || `${index + 1}th`} Installment`
}

/**
 * Gets the installment label based on index (for payment history - ALL installments).
 * First item is always "First Installment", last is always "Final Installment".
 */
function getInstallmentLabelForHistory(index: number, total: number): string {
  if (index === total - 1) {
    return 'Final Installment'
  }
  const ordinals = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth'
  ]
  return `${ordinals[index] || `${index + 1}th`} Installment`
}

export const {
  useGetPaymentPlanQuery,
  useGetPaymentScheduleQuery,
  useGetPaymentHistoryQuery,
  useGetBillingPeriodsQuery
} = paymentInstallmentsApi
